import { useEffect } from "react";
import { X, ShieldCheck, BookOpen, ExternalLink } from "lucide-react";

// ============================================================================
// ComplianceStatuteModal — pops the full IRS statute text behind an
// Audit-Safe / Partial-Deductible / Non-Deductible badge. Content is a
// paraphrased plain-English reading of the code sub-section keyed off the
// token attached to every receipt by the backend. This is a compliance UI
// affordance, NOT legal advice — the disclaimer footer says so plainly.
// ============================================================================

export const STATUTE_TEXT = {
  "IRC § 162(a)": {
    heading: "Ordinary & Necessary Business Expenses",
    body:
      "Section 162(a) permits a deduction for all ordinary and necessary expenses paid or incurred during the taxable year in carrying on any trade or business. The Supreme Court has interpreted 'ordinary' to mean common and accepted in the taxpayer's field, and 'necessary' to mean appropriate and helpful for the business. Substantiation via contemporaneous records (this receipt) is required under Treasury Regulation § 1.162-17.",
    reg_ref: "Treas. Reg. § 1.162-1; § 1.162-17",
    link: "https://www.law.cornell.edu/uscode/text/26/162",
  },
  "IRC § 162(a) + § 179": {
    heading: "Business Software · Section 179 Immediate Expensing",
    body:
      "Off-the-shelf computer software is qualified property under § 179(d)(1)(A)(ii). Rather than depreciating the cost over multiple years, § 179 permits an election to fully expense the software in the year placed in service, up to the annual § 179(b) dollar limitation. Ordinary-and-necessary substantiation under § 162(a) still applies.",
    reg_ref: "Treas. Reg. § 1.179-1; § 1.162-1",
    link: "https://www.law.cornell.edu/uscode/text/26/179",
  },
  "IRC § 179": {
    heading: "Section 179 Equipment Expensing",
    body:
      "Section 179 allows a taxpayer to elect to treat the cost of qualifying tangible personal property placed in service during the year as an expense — deductible in full up to the § 179(b)(1) dollar limitation. Qualifying property includes tangible personal property used more than 50% in a trade or business (computers, cameras, tools, printers, etc.).",
    reg_ref: "Treas. Reg. § 1.179-1; § 1.179-4",
    link: "https://www.law.cornell.edu/uscode/text/26/179",
  },
  "IRC § 162(a)(2)": {
    heading: "Away-From-Home Travel Expenses",
    body:
      "Section 162(a)(2) allows a deduction for traveling expenses (including amounts expended for meals and lodging other than amounts which are lavish or extravagant) while away from home in the pursuit of a trade or business. 'Away from home' generally means overnight and outside the taxpayer's tax home area.",
    reg_ref: "Treas. Reg. § 1.162-2; § 1.274-5",
    link: "https://www.law.cornell.edu/uscode/text/26/162",
  },
  "IRC § 162 + § 274(d)": {
    heading: "Business Transportation · Substantiation",
    body:
      "Business transportation costs (fuel, tolls, parking, ridesharing) are deductible under § 162, but § 274(d) imposes strict substantiation: no deduction is allowed unless the taxpayer substantiates the amount, time, place, and business purpose. Contemporaneous receipts satisfy this requirement.",
    reg_ref: "Treas. Reg. § 1.274-5T",
    link: "https://www.law.cornell.edu/uscode/text/26/274",
  },
  "IRC § 274(n)": {
    heading: "Business Meals · 50% Deductible Limit",
    body:
      "Section 274(n)(1) generally limits the deduction for food and beverage expenses to 50 percent of the amount otherwise allowable. The taxpayer or an employee of the taxpayer must be present at the meal, the meal must not be lavish or extravagant, and it must be provided in connection with an active trade or business.",
    reg_ref: "Treas. Reg. § 1.274-12",
    link: "https://www.law.cornell.edu/uscode/text/26/274",
  },
  "IRC § 274(a)(1)": {
    heading: "Entertainment · Post-TCJA Non-Deductible",
    body:
      "Under the Tax Cuts and Jobs Act of 2017, § 274(a)(1)(A) disallows any deduction for entertainment, amusement, or recreation expenses — even if directly related to the taxpayer's trade or business. Meals that meet the § 274(k) requirements remain 50% deductible; pure entertainment does not.",
    reg_ref: "Treas. Reg. § 1.274-11",
    link: "https://www.law.cornell.edu/uscode/text/26/274",
  },
  "IRC § 262(a)": {
    heading: "Personal Consumption · Non-Deductible",
    body:
      "Section 262(a) states that, except as otherwise expressly provided in the Code, no deduction shall be allowed for personal, living, or family expenses. Groceries, home utilities, personal clothing, and similar consumer purchases are personal in nature and fall under this exclusion.",
    reg_ref: "Treas. Reg. § 1.262-1",
    link: "https://www.law.cornell.edu/uscode/text/26/262",
  },
  "IRC § 170(c)": {
    heading: "Qualified Charitable Contribution",
    body:
      "Section 170(c) defines a charitable contribution as a contribution or gift to (or for the use of) a qualified organization — including 501(c)(3) public charities, governments, and certain religious/educational institutions. Cash contributions are deductible up to 60% of AGI; property contributions have separate limits under § 170(b).",
    reg_ref: "Treas. Reg. § 1.170A-1; § 1.170A-13",
    link: "https://www.law.cornell.edu/uscode/text/26/170",
  },
};

function textFor(token) {
  if (!token) return null;
  return STATUTE_TEXT[token.code] || {
    heading: token.title || "IRS Compliance Statute",
    body:
      "The category assigned to this expense is not currently mapped to a specific IRS statute in our compliance registry. Consult a qualified CPA for the precise legal basis of any deduction.",
    reg_ref: "—",
    link: "https://www.law.cornell.edu/uscode/text/26",
  };
}

export function ComplianceStatuteModal({ open, token, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !token) return null;
  const details = textFor(token);
  const variant = token.badge || "audit-safe";
  const accent =
    variant === "audit-safe"        ? { color: "#10b981", border: "rgba(16,185,129,0.45)", bg: "rgba(16,185,129,0.08)", label: "Audit-Safe · Fully Deductible" } :
    variant === "partial-deductible"? { color: "#f59e0b", border: "rgba(245,158,11,0.45)", bg: "rgba(245,158,11,0.08)", label: "Partial · 50% Deductible" } :
                                      { color: "#94a3b8", border: "rgba(148,163,184,0.35)", bg: "rgba(148,163,184,0.08)", label: "Personal · Non-Deductible" };

  return (
    <div
      data-testid="compliance-statute-modal"
      data-variant={variant}
      className="fixed inset-0 z-[9900] flex items-center justify-center px-4"
      style={{ background: "rgba(2,6,23,0.86)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6 relative overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(6,25,55,0.98))",
          border: `1px solid ${accent.border}`,
          boxShadow: `0 30px 70px -20px ${accent.border}`,
        }}
      >
        <button
          onClick={onClose}
          data-testid="compliance-statute-close-btn"
          className="absolute top-4 right-4 text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5"
          aria-label="Close statute detail"
        >
          <X size={16} />
        </button>
        <div
          className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em] px-2 py-1 rounded-md border"
          style={{ color: accent.color, borderColor: accent.border, background: accent.bg }}
        >
          <ShieldCheck size={11} /> {accent.label}
        </div>
        <h3 className="text-white text-xl font-bold mt-3 tracking-tight" data-testid="compliance-statute-heading">
          {token.code} · {details.heading}
        </h3>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">
          Deductible portion · <span style={{ color: accent.color }}>{token.deductible_pct}%</span> · Regs: {details.reg_ref}
        </div>
        <p
          data-testid="compliance-statute-body"
          className="mt-4 text-slate-200 text-sm leading-relaxed"
        >
          {details.body}
        </p>
        <a
          href={details.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="compliance-statute-source-link"
          className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-cyan-300 hover:text-cyan-100"
        >
          <BookOpen size={11} /> Read full statute on Cornell LII <ExternalLink size={10} />
        </a>
        <div className="mt-5 pt-4 border-t border-white/5 text-[10px] font-mono text-slate-500 italic leading-relaxed">
          Compliance guidance only — not legal or tax advice. Confirm every deduction with a qualified CPA.
        </div>
      </div>
    </div>
  );
}

export default ComplianceStatuteModal;
